<template>
  <transition name="app-download-fade">
    <div
      v-if="visible"
      class="app-download"
      role="presentation"
      @mousedown.self="close"
    >
      <section
        ref="dialog"
        class="app-download__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="app-download-title"
      >
        <header class="app-download__header">
          <button
            ref="closeButton"
            class="app-download__close"
            type="button"
            :aria-label="$t('关闭下载弹框')"
            @click="close"
          >
            <i class="el-icon-close" aria-hidden="true" />
          </button>
          <span class="app-download__hero-icon" aria-hidden="true">
            <i class="el-icon-download" />
          </span>
          <h2 id="app-download-title">{{ $t('下载 UPlus App') }}</h2>
          <p>{{ $t('扫码或点击下载，随时随地管理你的量化策略。') }}</p>
        </header>

        <div class="app-download__content">
          <div class="app-download__platforms">
            <article
              v-for="platform in platforms"
              :key="platform.key"
              class="app-download__card"
            >
              <div
                class="app-download__qr"
                :class="{ 'app-download__qr--ready': platform.qrCode }"
              >
                <span v-if="platform.qrCode" class="app-download__qr-image">
                  <img
                    :src="platform.qrCode"
                    :alt="`${platform.title} QR`"
                  />
                  <span class="app-download__qr-logo" aria-hidden="true">
                    <img src="@/assets/rebuild/qr-logo.png" alt="" />
                  </span>
                </span>
                <i v-else class="el-icon-full-screen" aria-hidden="true" />
              </div>

              <div class="app-download__platform-title">
                <span class="app-download__platform-icon" aria-hidden="true">
                  <svg v-if="platform.key === 'ios'" viewBox="0 0 24 24">
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08ZM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25Z" />
                  </svg>
                  <svg v-else viewBox="0 0 24 24">
                    <path d="M17.6 9.48H6.4c-.55 0-1 .45-1 1v6.7c0 .55.45 1 1 1h.82v2.26c0 .6.49 1.08 1.08 1.08s1.08-.49 1.08-1.08v-2.26h5.24v2.26c0 .6.49 1.08 1.08 1.08s1.08-.49 1.08-1.08v-2.26h.82c.55 0 1-.45 1-1v-6.7c0-.55-.45-1-1-1ZM8.83 5.7 7.46 3.33a.5.5 0 0 1 .18-.68.5.5 0 0 1 .68.18l1.42 2.46A6.35 6.35 0 0 1 12 4.88c.79 0 1.55.14 2.26.41l1.42-2.46a.5.5 0 0 1 .68-.18.5.5 0 0 1 .18.68L15.17 5.7A5.8 5.8 0 0 1 18.2 8.5H5.8a5.8 5.8 0 0 1 3.03-2.8ZM8.95 7.35a.65.65 0 1 0 0-1.3.65.65 0 0 0 0 1.3Zm6.1 0a.65.65 0 1 0 0-1.3.65.65 0 0 0 0 1.3ZM2.9 10.3c-.6 0-1.08.49-1.08 1.08v4.24c0 .6.49 1.08 1.08 1.08s1.08-.49 1.08-1.08v-4.24c0-.6-.49-1.08-1.08-1.08Zm18.2 0c-.6 0-1.08.49-1.08 1.08v4.24c0 .6.49 1.08 1.08 1.08s1.08-.49 1.08-1.08v-4.24c0-.6-.49-1.08-1.08-1.08Z" />
                  </svg>
                </span>
                <strong>{{ platform.title }}</strong>
              </div>
              <small>{{ platform.subtitle }}</small>
              <button
                class="app-download__button"
                type="button"
                :disabled="!platform.url"
                @click="openDownload(platform.url)"
              >
                <i class="el-icon-download" aria-hidden="true" />
                {{ platform.buttonText }}
              </button>
            </article>
          </div>

          <div class="app-download__safety">
            <i class="el-icon-lock" aria-hidden="true" />
            <span>{{ $t('请通过官方渠道下载，谨防山寨应用。') }}</span>
          </div>
        </div>
      </section>
    </div>
  </transition>
</template>

<script>
import { getAppVersionList } from '@/api/app-download'
import registeredAddress from '@/utils/registered_address'
import QRCode from 'qrcode'

function safeDownloadUrl(value) {
  if (typeof value !== 'string' || !value.trim()) return ''
  try {
    const parsed = new URL(value.trim())
    return ['https:', 'itms-services:', 'itms-apps:', 'market:'].includes(parsed.protocol)
      ? parsed.toString()
      : ''
  } catch (error) {
    return ''
  }
}

function safeQrCode(value) {
  if (typeof value !== 'string' || !value.trim()) return ''
  const source = value.trim()
  if (/^data:image\/(?:png|jpeg|jpg|webp);base64,/i.test(source)) return source
  try {
    const parsed = new URL(source)
    return parsed.protocol === 'https:' ? parsed.toString() : ''
  } catch (error) {
    return ''
  }
}

function responseItems(response) {
  if (Array.isArray(response)) return response
  if (response && Array.isArray(response.data)) return response.data
  if (response && response.data && Array.isArray(response.data.data)) return response.data.data
  return []
}

export default {
  name: 'AppDownloadModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      previousBodyOverflow: '',
      previousActiveElement: null,
      bodyLockActive: false,
      qrGenerationId: 0,
      generatedQrCodes: {
        ios: '',
        android: ''
      },
      links: {
        ios: {
          url: safeDownloadUrl(registeredAddress.download_ios_address()),
          qrCode: ''
        },
        android: {
          url: safeDownloadUrl(registeredAddress.download_android_address()),
          qrCode: ''
        }
      }
    }
  },
  computed: {
    platforms() {
      return [
        {
          key: 'ios',
          title: 'iOS',
          subtitle: 'iPhone / iPad',
          buttonText: this.$t('立即下载'),
          ...this.links.ios,
          qrCode: this.generatedQrCodes.ios || this.links.ios.qrCode
        },
        {
          key: 'android',
          title: 'Android',
          subtitle: this.$t('APK / 应用商店'),
          buttonText: this.$t('下载 APK'),
          ...this.links.android,
          qrCode: this.generatedQrCodes.android || this.links.android.qrCode
        }
      ]
    }
  },
  watch: {
    visible(visible) {
      if (visible) this.opened()
      else this.closed()
    }
  },
  beforeDestroy() {
    this.closed()
  },
  methods: {
    opened() {
      if (this.bodyLockActive) return
      this.bodyLockActive = true
      this.previousActiveElement = document.activeElement
      this.previousBodyOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      document.addEventListener('keydown', this.handleKeydown)
      this.generateQrCodes()
      this.loadDownloadLinks()
      this.$nextTick(() => {
        if (this.$refs.closeButton) this.$refs.closeButton.focus()
      })
    },
    closed() {
      if (!this.bodyLockActive) return
      document.removeEventListener('keydown', this.handleKeydown)
      document.body.style.overflow = this.previousBodyOverflow
      if (
        this.previousActiveElement &&
        document.documentElement.contains(this.previousActiveElement) &&
        typeof this.previousActiveElement.focus === 'function'
      ) {
        this.previousActiveElement.focus()
      }
      this.previousActiveElement = null
      this.bodyLockActive = false
    },
    close() {
      this.$emit('close')
    },
    handleKeydown(event) {
      if (event.key === 'Escape') this.close()
    },
    async loadDownloadLinks() {
      try {
        const response = await getAppVersionList()
        const nextLinks = { ...this.links }
        responseItems(response).forEach(item => {
          const type = String(item && item.type || '').toLowerCase()
          const key = type.includes('android')
            ? 'android'
            : (type.includes('ios') || type.includes('iphone') || type.includes('apple') ? 'ios' : '')
          if (!key) return
          nextLinks[key] = {
            url: safeDownloadUrl(item.download_url) || nextLinks[key].url,
            qrCode: safeQrCode(item.qr_code) || nextLinks[key].qrCode
          }
        })
        this.links = nextLinks
        this.generateQrCodes()
      } catch (error) {
        // Existing official download addresses remain available as a fallback.
      }
    },
    async generateQrCodes() {
      const generationId = ++this.qrGenerationId
      const makeQrCode = url => url
        ? QRCode.toDataURL(url, {
          errorCorrectionLevel: 'H',
          margin: 1,
          width: 220,
          color: {
            dark: '#0b1b3a',
            light: '#ffffff'
          }
        }).catch(() => '')
        : Promise.resolve('')
      const [ios, android] = await Promise.all([
        makeQrCode(this.links.ios.url),
        makeQrCode(this.links.android.url)
      ])
      if (generationId !== this.qrGenerationId) return
      this.generatedQrCodes = { ios, android }
    },
    openDownload(url) {
      if (!url) return
      if (/^(?:itms-services|itms-apps|market):/i.test(url)) {
        window.location.href = url
        return
      }
      const popup = window.open(url, '_blank', 'noopener,noreferrer')
      if (popup) popup.opener = null
    }
  }
}
</script>

<style scoped>
.app-download {
  position: fixed;
  z-index: 210;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(8, 16, 35, .56);
  backdrop-filter: blur(3px);
}

.app-download__dialog {
  width: min(520px, calc(100vw - 32px));
  max-height: calc(100vh - 32px);
  overflow: hidden;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 32px 80px -28px rgba(8, 16, 35, .55);
}

.app-download__header {
  position: relative;
  min-height: 156px;
  padding: 28px 28px 26px;
  color: #fff;
  background-color: #10265a;
  background-image: radial-gradient(circle at 2px 2px, rgba(255, 255, 255, .14) 1px, transparent 0);
  background-size: 24px 24px;
}

.app-download__close {
  position: absolute;
  top: 16px;
  right: 16px;
  display: grid;
  width: 32px;
  height: 32px;
  padding: 0;
  place-items: center;
  color: #fff;
  border: 0;
  border-radius: 9px;
  background: rgba(255, 255, 255, .16);
  cursor: pointer;
}

.app-download__close:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 2px;
}

.app-download__hero-icon {
  display: inline-grid;
  width: 46px;
  height: 46px;
  place-items: center;
  color: #afcbff;
  border-radius: 12px;
  background: rgba(82, 126, 214, .36);
  font-size: 22px;
}

.app-download__header h2 {
  margin: 18px 0 0;
  font-size: 21px;
  font-weight: 800;
  line-height: 1.2;
}

.app-download__header p {
  margin: 8px 0 0;
  color: #b9c5d8;
  font-size: 14px;
  line-height: 1.55;
}

.app-download__content {
  max-height: calc(100vh - 188px);
  overflow-y: auto;
  padding: 20px;
}

.app-download__platforms {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.app-download__card {
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 18px 18px 16px;
  text-align: center;
  border: 1px solid #e3e9f4;
  border-radius: 14px;
}

.app-download__qr {
  display: grid;
  width: min(112px, 100%);
  aspect-ratio: 1;
  place-items: center;
  color: #b7c8e4;
  border: 1px solid #dce5f4;
  border-radius: 11px;
  background: #eef3ff;
  font-size: 34px;
}

.app-download__qr--ready {
  overflow: hidden;
  border-color: transparent;
  background: #fff;
}

.app-download__qr-image {
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
}

.app-download__qr-image > img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.app-download__qr-logo {
  position: absolute;
  top: 50%;
  left: 50%;
  display: grid;
  width: 29px;
  height: 29px;
  place-items: center;
  border-radius: 5px;
  background: #fff;
  transform: translate(-50%, -50%);
}

.app-download__qr-logo img {
  display: block;
  width: 28px;
  height: 28px;
  object-fit: contain;
}

.app-download__platform-title {
  display: inline-flex;
  height: 22px;
  align-items: center;
  justify-content: center;
  gap: 7px;
  margin-top: 13px;
  color: #14223c;
  font-size: 15px;
  line-height: 1;
}

.app-download__platform-icon,
.app-download__platform-icon svg {
  display: block;
  width: 20px;
  height: 20px;
}

.app-download__platform-icon svg {
  fill: currentColor;
}

.app-download__card small {
  margin-top: 3px;
  color: #8492a8;
  font-size: 12.5px;
  line-height: 1.3;
}

.app-download__button {
  display: inline-flex;
  width: 100%;
  height: 38px;
  align-items: center;
  justify-content: center;
  gap: 7px;
  margin-top: 14px;
  color: #fff;
  border: 0;
  border-radius: 9px;
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  font-size: 13.5px;
  font-weight: 700;
  cursor: pointer;
}

.app-download__button:disabled {
  cursor: not-allowed;
  opacity: .58;
}

.app-download__safety {
  display: flex;
  min-height: 44px;
  align-items: center;
  gap: 9px;
  margin-top: 20px;
  padding: 11px 14px;
  color: #4d607d;
  border-radius: 10px;
  background: #eef3ff;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.35;
}

.app-download-fade-enter-active,
.app-download-fade-leave-active {
  transition: opacity .18s ease;
}

.app-download-fade-enter-active .app-download__dialog,
.app-download-fade-leave-active .app-download__dialog {
  transition: transform .18s ease, opacity .18s ease;
}

.app-download-fade-enter,
.app-download-fade-leave-to {
  opacity: 0;
}

.app-download-fade-enter .app-download__dialog,
.app-download-fade-leave-to .app-download__dialog {
  opacity: 0;
  transform: translateY(8px) scale(.98);
}

@media (max-width: 760px) {
  .app-download {
    align-items: flex-end;
    padding: 0;
  }

  .app-download__dialog {
    width: 100%;
    max-height: calc(100vh - 12px);
    max-height: calc(100dvh - 12px);
    border-radius: 20px 20px 0 0;
  }

  .app-download-fade-enter-active .app-download__dialog,
  .app-download-fade-leave-active .app-download__dialog {
    transition: transform .28s cubic-bezier(.22, 1, .36, 1);
  }

  .app-download-fade-enter .app-download__dialog,
  .app-download-fade-leave-to .app-download__dialog {
    opacity: 1;
    transform: translateY(100%);
  }

  .app-download__header {
    min-height: 142px;
    padding: 22px 20px 20px;
  }

  .app-download__hero-icon {
    width: 42px;
    height: 42px;
  }

  .app-download__header h2 {
    margin-top: 14px;
  }

  .app-download__content {
    max-height: calc(100vh - 154px);
    max-height: calc(100dvh - 154px);
    padding: 16px 16px calc(16px + env(safe-area-inset-bottom));
  }

  .app-download__platforms {
    gap: 10px;
  }

  .app-download__card {
    padding: 14px 12px 12px;
  }

  .app-download__qr {
    width: min(102px, 100%);
  }
}
</style>

